import styled, { css } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

/* At least one screen tall, so the fixed bar sits at the bottom even on short steps (as StickyBarPage) */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - var(--app-bottom-nav-height, 0px));
`;

/* One step of the flow: progress, question and its answers, then the step's actions */
export const Step = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 640px;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }

  .progress {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-neutral-150);

    .bar {
      height: 4px;
      overflow: hidden;
      background: var(--color-neutral-dark);
      border-radius: 100px;

      span {
        display: block;
        height: 100%;
        background: var(--color-brand-plus);
        border-radius: 100px;
        transition: width 0.25s;
      }
    }
  }

  h3 {
    font-family: Poppins, sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
    color: var(--color-neutral-white);
  }

  .description {
    margin-top: -0.75rem;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }
`;

export const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

/* An answer: a round mark for single choice, a square one for multiple choice */
export const Option = styled.button<{ $selected: boolean; $multiple?: boolean }>`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.875rem 1rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25rem;
  color: var(--color-neutral-white);
  text-align: left;
  background: var(--color-neutral-dark);
  border: 1px solid ${props => (props.$selected ? "var(--color-brand-plus)" : "transparent")};
  border-radius: 12px;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${props => (props.$selected ? "var(--color-brand-plus)" : "var(--color-neutral-base)")};
  }

  &:focus-visible {
    outline: 2px solid var(--color-brand-plus);
    outline-offset: 2px;
  }

  .mark {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border: 2px solid ${props => (props.$selected ? "var(--color-brand-plus)" : "var(--color-neutral-light)")};
    border-radius: ${props => (props.$multiple ? "5px" : "100%")};

    &::after {
      width: 8px;
      height: 8px;
      content: "";
      background: ${props => (props.$selected ? "var(--color-brand-plus)" : "transparent")};
      border-radius: ${props => (props.$multiple ? "2px" : "100%")};
    }
  }
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-neutral-150);

  textarea {
    min-height: 88px;
  }

  textarea,
  input {
    padding: 0.75rem 1rem;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    resize: vertical;
    background: var(--color-neutral-dark);
    border: 1px solid var(--color-neutral-base);
    border-radius: 12px;

    &:focus {
      border-color: var(--color-brand-plus);
      outline: none;
    }
  }
`;

/* 1 to 5 */
export const Scale = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .values {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .labels {
    display: flex;
    justify-content: space-between;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }
`;

export const ScaleValue = styled.button<{ $selected: boolean }>`
  height: 48px;
  font-family: Inter, sans-serif;
  font-size: 1rem;
  font-weight: 800;
  color: ${props => (props.$selected ? "var(--color-neutral-darkness)" : "var(--color-neutral-white)")};
  background: ${props => (props.$selected ? "var(--color-brand-plus)" : "var(--color-neutral-dark)")};
  border-radius: 12px;
  transition: background-color 0.15s;

  &:hover {
    background: ${props => (props.$selected ? "var(--color-brand-plus)" : "var(--color-neutral-base)")};
  }
`;

/* A retention offer; the discount gets the green glow of the other PLUS blocks */
export const OfferCard = styled.div<{ $plus?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  padding: 1.25rem;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  ${props =>
    props.$plus &&
    css`
      background:
        radial-gradient(120% 140% at 0% 0%, rgba(87, 242, 139, 0.2) 0%, rgba(87, 242, 139, 0) 60%),
        var(--color-neutral-dark);
      box-shadow: inset 0 0 0 1px rgba(87, 242, 139, 0.3);
    `}

  h4 {
    font-family: Poppins, sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-neutral-white);
  }

  p {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }

  .error {
    color: var(--color-feedback-danger);
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    margin-top: 0.25rem;
  }

  li button {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 0.5rem 0;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    text-align: left;

    span {
      color: var(--color-brand-plus);
    }
  }
`;

/* What the user loses, as a short list */
export const Losses = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.25rem;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  li {
    display: flex;
    gap: 0.625rem;
    align-items: center;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    color: var(--color-neutral-white);

    &::before {
      flex-shrink: 0;
      width: 6px;
      height: 6px;
      content: "";
      background: var(--color-feedback-danger);
      border-radius: 100%;
    }
  }
`;

export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  color: var(--color-neutral-150);

  &.error {
    color: var(--color-feedback-danger);
  }
`;

export const PlusButton = styled.button`
  padding: 0.75rem 1.25rem;
  margin-top: 0.25rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 800;
  color: var(--color-neutral-darkness);
  background: var(--color-brand-plus);
  border-radius: 100px;
  transition:
    filter 0.15s,
    opacity 0.15s;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
`;

export const SecondaryButton = styled.button`
  padding: 0.75rem 1.25rem;
  margin-top: 0.25rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 800;
  color: var(--color-neutral-white);
  background: var(--color-neutral-base);
  border-radius: 100px;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.15);
  }
`;
